import * as Sentry from '@sentry/browser'

import { InstrumentType } from '../types'

export enum ContentType {
  ApplicationJson = 'application/json',
  FormUrlEncoded = 'application/x-www-form-urlencoded',
  Html = 'text/html; charset=utf-8',
}

function parseResponseForContentType(response: string, contentType: ContentType) {
  switch (contentType) {
    case ContentType.ApplicationJson:
    case ContentType.FormUrlEncoded:
      return JSON.parse(response)
    default:
      return response
  }
}

export class ThrowableError extends Error {
  status: number
  isServerError = false
  extra: Record<string, any> = {}

  constructor(status: number, message = 'ThrowableError', extra: Record<string, any> = {}) {
    super(`${status} ${message}`)
    this.status = status
    this.isServerError = status >= 500
    this.extra = extra
  }
}

export interface CancelablePromise<ResolveType> extends Promise<ResolveType> {
  cancel?: () => void
}

/**
 * General api fetch utility which returns a cancelable promise
 */
export function apiRequest<ResolveType>(
  url: string,
  { query, headers = {}, ...options }: RequestInit & { query?: Record<string, any> } = {},
  throwOnAllErrors = false
): CancelablePromise<ResolveType | undefined> {
  const controller = 'AbortController' in window ? new AbortController() : undefined
  const requestUrl = query ? `${url}?${new URLSearchParams(query)}` : url
  const contentType: ContentType = (headers as any)['Content-Type'] ?? ContentType.ApplicationJson

  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': contentType,
      ...headers,
    },
  }
  const request = fetch(requestUrl, { ...requestOptions, signal: controller?.signal })

  const cancelableRequest: CancelablePromise<ResolveType> = request
    .then(async (response) => {
      const textResponse = await response.text()
      const reponseContentType: ContentType = (response.headers.get('Content-Type') as any) ?? ContentType.Html
      if (!response.ok || response.status >= 500) {
        throw new ThrowableError(response.status, response.statusText, { rayId: response.headers.get('cf-ray') })
      }
      try {
        return parseResponseForContentType(textResponse, reponseContentType)
      } catch (error: any) {
        throw new ThrowableError(500, error.message, { rayId: response.headers.get('cf-ray') })
      }
    })
    .catch((error: ThrowableError) => {
      if (error.status >= 500) {
        Sentry.withScope((scope) => {
          scope.setContext('Cloudflare', error.extra)

          if (error.status === 500) {
            Sentry.captureException(error)
          } else {
            Sentry.captureMessage(`${error.status} ${error.message}`)
          }
        })
      }
      if (throwOnAllErrors) {
        throw error
      }
    })

  if (controller) {
    cancelableRequest.cancel = () => controller.abort()
  }

  return cancelableRequest
}

export interface SearchApiResult {
  ticker: string
  company: string
  exchange: string
}

export function searchApiRequest(input = '', instrument?: InstrumentType) {
  return apiRequest<SearchApiResult[]>('/api/suggestions.ashx', { query: { input, instrument } }, true)
}
